import { AdminMembersReportPageStartDate } from "@/admin/insights/member-reports/AdminMembersReportPage"
import { AdminMembersReportDailyTimeSpentChartQuery } from "@/admin/insights/member-reports/tabs/overview/__generated__/AdminMembersReportDailyTimeSpentChartQuery.graphql"
import { useLabels } from "@/core/context/LabelsContext"
import { StateMembershipSessionActivityArea } from "@/core/session/__generated__/MembershipSessionProvider_recordSessionLocationMutation.graphql"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import {
  DiscoChartTooltipContent,
  DiscoEmptyState,
  DiscoLineChart,
  DiscoSection,
  DiscoSpinner,
  DiscoText,
} from "@disco-ui"
import DateUtils from "@utils/date/dateUtils"
import { DATE_FORMAT } from "@utils/time/timeConstants"
import { formatDateWithOptions } from "@utils/time/timeUtils"
import { ParentSize } from "@visx/responsive"
import { graphql } from "react-relay"

type TimeSpentOnDay = {
  date: string
  totalTimeSpentSeconds: number
  totalSessions: number
}

interface Props {
  organizationMembershipId: GlobalID
  filters: {
    startDate: AdminMembersReportPageStartDate
    area: StateMembershipSessionActivityArea
    selectedProductId: GlobalID | null
  }
}

const AdminMembersReportDailyTimeSpentChart = ({
  organizationMembershipId,
  filters,
}: Props) => {
  const classes = useStyles()
  const labels = useLabels()

  const [{ node }] =
    Relay.useRefetchableQuery<AdminMembersReportDailyTimeSpentChartQuery>(
      graphql`
        query AdminMembersReportDailyTimeSpentChartQuery(
          $id: ID!
          $startDate: String
          $area: StateMembershipSessionActivityArea!
          $productId: ID
        ) {
          node(id: $id) {
            __typename
            ... on OrganizationMembership {
              id
              __typename
              dailyTimeSpentReport(
                startDate: $startDate
                area: $area
                productId: $productId
              ) {
                data {
                  date
                  totalTimeSpentSeconds
                  totalSessions
                }
              }
            }
          }
        }
      `,
      {
        id: organizationMembershipId,
        startDate: filters.startDate,
        area: filters.area,
        productId: filters.selectedProductId,
      },
      {
        fetchPolicy: "network-only",
      }
    )

  const organizationMembership = Relay.narrowNodeType(node, "OrganizationMembership")
  if (!organizationMembership) return null

  const { data } = organizationMembership.dailyTimeSpentReport as {
    data: TimeSpentOnDay[] | null
  }

  return data?.length ? (
    <ParentSize className={classes.graphSvg}>
      {({ width, height }) => (
        <DiscoLineChart
          width={width}
          height={height}
          data={data}
          xAccessor={(d) => d.date}
          yAccessor={(d) => d.totalTimeSpentSeconds / 3600}
          renderTooltip={({ tooltipData }, xAccessor, yAccessor) => {
            if (!tooltipData?.nearestDatum?.datum) return
            const x = xAccessor(tooltipData.nearestDatum.datum)
            const y = yAccessor(tooltipData.nearestDatum.datum) * 3600 // Total time spent in seconds

            const dateData = data.find((d) => d.date === x)!
            return (
              <DiscoChartTooltipContent>
                <DiscoText color={"common.white"} variant={"body-sm-600"}>
                  {formatDateWithOptions({
                    format: DATE_FORMAT.DEFAULT,
                  })(new Date(x))}
                </DiscoText>
                <DiscoText
                  color={"common.white"}
                  variant={"body-sm-600"}
                >{`- Total Sessions: ${dateData.totalSessions || "-"}`}</DiscoText>
                <DiscoText color={"common.white"} variant={"body-sm-600"}>
                  {`- Total Time Spent: ${y ? DateUtils.formatSecondsToHMS(y) : "-"}`}
                </DiscoText>
                <DiscoText
                  color={"common.white"}
                  variant={"body-sm-600"}
                >{`- Average Session Duration: ${
                  y ? DateUtils.formatSecondsToHMS(y / dateData.totalSessions) : "-"
                }`}</DiscoText>
              </DiscoChartTooltipContent>
            )
          }}
        />
      )}
    </ParentSize>
  ) : (
    <DiscoEmptyState
      testid={"AdminMembersReportDailyTimeSpentChart.EmptyState"}
      subtitle={`${labels.admin_member.singular} has not spent any time in the ${
        filters.area === "community"
          ? labels.organization.singularLowercase
          : labels.admin_experience.singular
      } yet.`}
    />
  )
}

export const AdminMembersReportDailyTimeSpentChartSkeleton = () => {
  const classes = useStyles()
  return (
    <DiscoSection className={classes.root} padding={3}>
      <DiscoSpinner absoluteCenter />
    </DiscoSection>
  )
}

const useStyles = makeUseStyles((theme) => ({
  root: {
    position: "relative",
    height: 400,
  },
  graphSvg: {
    paddingBottom: theme.spacing(9),
  },
}))

export default Relay.withSkeleton({
  component: AdminMembersReportDailyTimeSpentChart,
  skeleton: () => <DiscoSpinner />,
})
